import { Spinner } from '@frontend/components/common';
import { ICONS } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import { SlotStatus } from '@frontend/slot/types';
import { Suspense, lazy, useState } from 'react';
import { LuCheck, LuX } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';

import { useWizardModalScreenNavigation } from '../../wizard-modal-navigation.context';
import { SelectLocationSpotLayout } from './select-location-spot-layout.component';
import useSelectLocation from './select-location.controller';

const ID = 'select-location';
const AvailableSlotsSelect = lazy(() => import('@frontend/slot/select').then((module) => ({ default: module.AvailableSlotsSelect })));
const AvailableSpotsSelect = lazy(() => import('@frontend/slot/select').then((module) => ({ default: module.AvailableSpotsSelect })));

const SelectLocation = (props: { wizard_id: string }) => {
    const viewProps = useSelectLocation(props);
    const [showLayout, setShowLayout] = useState(false);

    useWizardModalScreenNavigation({
        onContinue: viewProps.onSubmit,
        continueDisabled: viewProps.submitDisabled
    });

    return (
        <div
            id={ID}
            className={`flex w-full flex-col gap-3 ${showLayout ? 'h-full min-h-0' : ''}`}>
            <div className='flex items-start justify-start self-stretch'>
                <div className='text-foreground-default justify-start text-base font-medium'>{CommonMessage.FORMS.PROVIDE_THE_FOLLOWING}</div>
            </div>

            <div
                data-state='Open'
                className={`outline-border-default inline-flex flex-col items-start justify-start gap-5 self-stretch rounded-xl p-4 outline-1 ${
                    showLayout ? 'min-h-0 flex-1' : ''
                }`}>
                <div className='inline-flex items-center justify-between self-stretch'>
                    <div className='flex items-center justify-start gap-4'>
                        <div className='outline-border-default relative flex size-12 items-center justify-center rounded-full outline-2'>
                            {viewProps.spotId ? (
                                <LuCheck className='bg-status-success text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                            ) : (
                                <LuX className='bg-brand-muted text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                            )}
                            <ICONS.ENTITY.SPOT className='size-1/2' />
                        </div>
                        <div className='inline-flex flex-col items-start justify-center gap-1'>
                            <div className='text-foreground-default justify-start text-base font-semibold'>{CommonMessage.OBJECTS.SPOT.DISTRI_SPOT}</div>
                        </div>
                    </div>
                </div>

                <Suspense fallback={<Spinner />}>
                    <div className={`flex w-full flex-col gap-2 ${showLayout ? 'min-h-0 flex-1' : ''}`}>
                        <AvailableSpotsSelect
                            key={viewProps.spotId}
                            value={viewProps.spotId}
                            onChange={(value) => {
                                if (value !== viewProps.spotId) {
                                    viewProps.setSlotId(null);
                                }
                                viewProps.setSpotId(value);
                            }}
                            constraints={viewProps.availableSpotConstraints}
                            hideLabel
                        />
                        {viewProps.spotId && (
                            <AvailableSlotsSelect
                                key={`slot-${viewProps.spotId}-${viewProps.availableSlotConstraints.functionality_id ?? 'none'}`}
                                value={viewProps.slotId}
                                onChange={viewProps.setSlotId}
                                submitted={false}
                                spot_id={viewProps.spotId}
                                constraints={viewProps.availableSlotConstraints}
                                fetchSlots={viewProps.fetchAvailableSlots}
                                queryParams={{ spot_id: viewProps.spotId, status: SlotStatus.AVAILABLE }}
                                hideLabel
                                isClearable
                            />
                        )}
                        {viewProps.spotId && (
                            <button
                                type='button'
                                className='text-brand-primary self-start text-sm font-medium underline'
                                onClick={() => setShowLayout((prev) => !prev)}>
                                {showLayout ? (
                                    <FormattedMessage
                                        id='platform.wizard.select-location.hide-layout-view'
                                        description='Button label to hide the slot layout view in the location selection screen'
                                        defaultMessage='Hide layout view'
                                    />
                                ) : (
                                    <FormattedMessage
                                        id='platform.wizard.select-location.show-layout-view'
                                        description='Button label to show the slot layout view in the location selection screen'
                                        defaultMessage='Show layout view'
                                    />
                                )}
                            </button>
                        )}
                        {showLayout && (
                            <SelectLocationSpotLayout
                                spotId={viewProps.spotId}
                                slotId={viewProps.slotId}
                                availableSlotConstraints={viewProps.availableSlotConstraints}
                                setSlotId={viewProps.setSlotId}
                            />
                        )}
                    </div>
                </Suspense>
            </div>
        </div>
    );
};

export default SelectLocation;
